import { gql } from '@apollo/client';

import { COMMENT, REPLY } from './comment';
import { COUNTRY } from './country';
import { CRITERIA, CRITERIA_RESULT } from './criteria';
import { DISCUSSION } from './discussion';
import { IDEA, IDEA_BASIC, IDEA_COLLABORATION, IDEA_REPORTS } from './idea';
import { METRIC, PROJECT, UNIT } from './idea-metrics';
import { IDEA_METRIC } from './idea-metrics';
import { INNOVATION } from './innovation';
import { REGION } from './region';
import { TOPIC } from './topic';
import { FILE } from './upload';
import { RANK, USER_BASICS } from './user';

export const IDEA_REVIEWER = gql`
  fragment ideaReviewerList on IdeaReviewerList {
    id
    fullName
    email
    description
    role
    addType
    reviewerTag {
      id
      name
    }
    channelId
    user {
      ...userBasics
    }
  }
`;

export const PHASE = gql`
  fragment phase on Phase {
    id
    title
    description
    orderNumber
  }
`;

export const CHANNEL = gql`
  fragment channel on Channel {
    id
    title
    ideasCount
    channelPhases {
      phase {
        ...phase
      }
    }
  }
`;

export const CHANNEL_BASICS = gql`
  fragment channelBasics on Channel {
    id
    title
    ideasCount
  }
`;

export const CHANNEL_TAG = gql`
  fragment channelTag on ChannelTag {
    id
    name
  }
`;

export const CHANNEL_IDEA_EVALUATION_RESULT = gql`
  fragment channelCriteriaResults on ChannelIdeaEvaluation {
    status
    score
    createdBy {
      ...userBasics
    }
    criteria {
      ...criteria
    }
  }
`;

export const CREATE_CHANNEL = gql`
  mutation CreateChannel($input: CreateChannelInput!) {
    createChannel(input: $input) {
      ...channel
    }
  }
  ${CHANNEL}
  ${PHASE}
`;

export const PATCH_CHANNEL = gql`
  mutation PatchChannel($input: PatchChannelInput!) {
    patchChannel(input: $input) {
      ...channel
    }
  }
  ${CHANNEL}
  ${PHASE}
`;

export const CREATE_PHASE = gql`
  mutation CreatePhase($input: CreatePhaseInput!) {
    createPhase(input: $input) {
      ...phase
    }
  }
  ${PHASE}
`;

export const PATCH_PHASE = gql`
  mutation PatchPhase($input: PatchPhaseInput!) {
    patchPhase(input: $input) {
      ...phase
    }
  }
  ${PHASE}
`;

export const PATCH_IDEA_PHASE = gql`
  mutation PatchIdeaPhase($channelId: UUID!, $ideaId: UUID!, $phaseId: UUID!) {
    patchIdeaPhase(channelId: $channelId, ideaId: $ideaId, phaseId: $phaseId)
  }
`;

export const CREATE_CHANNEL_TAG = gql`
  mutation CreateChannelTag($name: String!) {
    createChannelTag(name: $name) {
      ...channelTag
    }
  }
  ${CHANNEL_TAG}
`;

export const DELETE_CHANNEL_TAG = gql`
  mutation DeleteChannelTag($id: UUID!) {
    deleteChannelTag(id: $id)
  }
`;

export const CREATE_IDEA_CHANNEL_TAG = gql`
  mutation CreateIdeaChannelTag($ideaId: UUID!, $channelTagId: UUID!) {
    createIdeaChannelTag(ideaId: $ideaId, channelTagId: $channelTagId) {
      id {
        ideaId
        channelTagId
      }
      channelTag {
        ...channelTag
      }
    }
  }
  ${CHANNEL_TAG}
`;

export const DELETE_IDEA_CHANNEL_TAG = gql`
  mutation DeleteIdeaChannelTag($ideaId: UUID!, $channelTagId: UUID!) {
    deleteIdeaChannelTag(ideaId: $ideaId, channelTagId: $channelTagId)
  }
`;

export const ASSIGN_CHANNEL_MEMBER = gql`
  mutation AssignChannelMember($input: AssignChannelMemberInput) {
    assignChannelMember(input: $input) {
      id
      user {
        ...userBasics
      }
      permission {
        id
        name
        type
        resource {
          id
          name
        }
      }
      resourceId
      roles {
        id
        name
      }
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const UNASSIGN_CHANNEL_MEMBER = gql`
  mutation UnassignFromChannel($input: UnassignChannelMemberInput!) {
    unassignFromChannel(input: $input)
  }
`;

export const UNASSIGN_CHANNEL_IDEA_MEMBER = gql`
  mutation UnassignFromIdea($input: UnassignIdeaMemberInput!) {
    unassignFromIdea(input: $input)
  }
`;

export const SETTLE_IDEA_REVIEWER_REQUEST = gql`
  mutation SettleIdeaReviewRequest($input: SettleIdeaReviewInput) {
    settleIdeaReviewRequest(input: $input) {
      id
    }
  }
`;

export const CREATE_REVIEWER_TAG = gql`
  mutation CreateReviewerTag($input: CreateReviewerTagInput!) {
    createReviewerTag(input: $input) {
      id
      name
    }
  }
`;

export const ADD_TO_REVIEWER_LIST = gql`
  mutation AddToReviewerList($input: AddToReviewerListInput!) {
    addToReviewerList(input: $input) {
      ...ideaReviewerList
    }
  }
  ${IDEA_REVIEWER}
  ${USER_BASICS}
  ${FILE}
`;

export const UPDATE_REVIEWER_LIST_ITEM = gql`
  mutation UpdateReviewerListItem($input: EditReviewerList!) {
    updateReviewerListItem(input: $input) {
      ...ideaReviewerList
    }
  }
  ${IDEA_REVIEWER}
  ${USER_BASICS}
  ${FILE}
`;

export const DELETE_REVIEWER_LIST_ITEM = gql`
  mutation DeleteReviewerListItem($input: RemoveFromReviewerListInput!) {
    deleteReviewerListItem(input: $input)
  }
`;

export const ADD_SUGGESTED_REVIEWER = gql`
  mutation AddSuggestedReviewer($input: AddSuggestedReviewer) {
    addSuggestedReviewer(input: $input) {
      id
      ideaId
      userId
      firstName
      lastName
      description
      email
      suggestedBy
    }
  }
`;

export const DELETE_SUGGESTED_REVIEWER = gql`
  mutation RemoveSuggestedReviewer($id: UUID) {
    removeSuggestedReviewer(id: $id)
  }
`;

export const LIST_SUGGESTED_REVIEWERS = gql`
  query ListSuggestedReviewers(
    $ideaId: UUID
    $page: Int = 1
    $limit: Int = 10
  ) {
    listSuggestedReviewers(ideaId: $ideaId, page: $page, limit: $limit) {
      id
      ideaId
      userId
      firstName
      lastName
      description
      email
      suggestedBy
    }
  }
`;

export const SEND_IDEA_REVIEWER_REQUEST = gql`
  mutation SendIdeaReviewRequest($input: RequestIdeaReviewInput!) {
    sendIdeaReviewRequest(input: $input) {
      id
      user {
        ...userBasics
      }
      idea {
        id
      }
      requestedBy {
        ...userBasics
      }
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const PATCH_PHASE_ORDER = gql`
  mutation PatchPhaseOrder($ids: [String]!) {
    patchPhaseOrder(ids: $ids) {
      ...phase
    }
  }
  ${PHASE}
`;

export const GET_CHANNELS = gql`
  query Channels {
    channels {
      ...channelBasics
    }
  }
  ${CHANNEL_BASICS}
`;

export const GET_MANAGER_CHANNELS = gql`
  query ChallengeManagerChannels {
    challengeManagerChannels {
      id
      title
    }
  }
`;

export const GET_CHANNEL_IDEA_RESULTS = gql`
  query Channels {
    channels {
      id
      title
      channelPhases {
        ideas {
          ...idea
          channelCriteria {
            ...criteria
          }
          channelCriteriaResults {
            ...channelCriteriaResults
          }
          ideaChannelMembers {
            user {
              ...userBasics
            }
          }
        }
      }
    }
  }
  ${IDEA}
  ${INNOVATION}
  ${IDEA_COLLABORATION}
  ${REGION}
  ${TOPIC}
  ${DISCUSSION}
  ${COUNTRY}
  ${IDEA_REPORTS}
  ${FILE}
  ${USER_BASICS}
  ${RANK}
  ${CRITERIA}
  ${CHANNEL_IDEA_EVALUATION_RESULT}
`;

export const GET_IDEA_RESULTS_BY_ID = gql`
  query ChannelIdea($ideaId: UUID!) {
    channelIdea(ideaId: $ideaId) {
      id
      channelCriteria {
        ...criteria
      }
      channelCriteriaResults {
        ...channelCriteriaResults
      }
      channel {
        id
      }
      internalDiscussion {
        ...discussion
        __typename
      }
    }
  }
  ${FILE}
  ${USER_BASICS}
  ${CRITERIA}
  ${CHANNEL_IDEA_EVALUATION_RESULT}
  ${DISCUSSION}
  ${COMMENT}
  ${REPLY}
`;

export const GET_CHANNEL_BY_ID = gql`
  query Channel($id: UUID!) {
    channel(id: $id) {
      id
      title
      ideasCount
      channelPhases {
        phase {
          ...phase
        }
      }
    }
  }
  ${PHASE}
`;

export const GET_CHANNEL_TAGS = gql`
  query ChannelTags {
    channelTags {
      ...channelTag
    }
  }
  ${CHANNEL_TAG}
`;

export const GET_CHANNEL_REVIEWERS = gql`
  query FindTotalChannelReviewers($input: ListChannelReviewers) {
    findTotalChannelReviewers(input: $input) {
      reviewers {
        user {
          ...userBasics
        }
      }
      total
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const GET_CHANNEL_MEMBERS = gql`
  query GetChannelMembers(
    $channelMemberType: ChannelMemberType
    $channelId: UUID!
  ) {
    getChannelMembers(
      channelMemberType: $channelMemberType
      channelId: $channelId
    ) {
      user {
        ...userBasics
      }
      permission {
        resource {
          name
        }
      }
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const GET_IDEA_REVIEWER_LIST = gql`
  query IdeaReviewerLists($input: IdeaReviewerListInput) {
    ideaReviewerLists(input: $input) {
      ideaReviewerListItem {
        ...ideaReviewerList
      }
      requestStatus
    }
  }
  ${IDEA_REVIEWER}
  ${USER_BASICS}
  ${FILE}
`;

export const GET_IDEA_REVIEWER_BY_ID = gql`
  query GetIdeaReviewerItem($channelId: UUID!, $id: UUID) {
    getIdeaReviewerItem(channelId: $channelId, id: $id) {
      ...ideaReviewerList
    }
  }
  ${IDEA_REVIEWER}
  ${USER_BASICS}
  ${FILE}
`;

export const GET_REVIEWER_TAGS = gql`
  query ListReviewerTag($channelId: UUID!) {
    listReviewerTag(channelId: $channelId) {
      id
      name
    }
  }
`;

export const GET_IDEA_ACTIVITY_LOG = gql`
  query ActivityLogs($ideaId: UUID!) {
    activityLogs(ideaId: $ideaId) {
      id
      type
      action
      newValue
      oldValue
      createdAt
      created {
        ...userBasics
      }
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const GET_IDEA_MEMBERS = gql`
  query GetIdeaMembers($ideaId: UUID!) {
    getIdeaMembers(ideaId: $ideaId) {
      id
      user {
        ...userBasics
      }
      permission {
        id
        name
        type
        resource {
          id
          name
        }
      }
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const GET_IDEA_REVIEW_REQUESTS = gql`
  query IdeaReviewRequests($ideaId: UUID!) {
    ideaReviewRequests(ideaId: $ideaId) {
      id
      user {
        ...userBasics
      }
      requestedBy {
        ...userBasics
      }
      createdAt
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const CREATE_CHANNEL_CHALLENGE = gql`
  mutation CreateChallengeChannel($channelId: UUID!, $challengeId: UUID!) {
    createChallengeChannel(channelId: $channelId, challengeId: $challengeId) {
      id
      channelId
      challengeId
    }
  }
`;

export const GET_IDEA_COUNTS_BY_STAGES = gql`
  query ChannelIdeaCountByStage($input: ChannelIdeaStagesCountInput) {
    channelIdeaCountByStage(input: $input) {
      phase
      items
    }
  }
`;

export const GET_IDEA_COUNT_BY_STATUS = gql`
  query IdeaPhaseCountByDates(
    $startDate: String
    $endDate: String
    $channelId: UUID!
    $challengeId: UUID
    $spaceId: UUID
  ) {
    ideaPhaseCountByDates(
      startDate: $startDate
      endDate: $endDate
      channelId: $channelId
      challengeId: $challengeId
      spaceId: $spaceId
    ) {
      stageCount {
        items
        phase
      }
      days
    }
  }
`;

export const GET_IDEA_COUNT_BY_DAYS = gql`
  query IdeaCountByPhaseAndDays(
    $channelId: UUID!
    $challengeId: UUID
    $spaceId: UUID
  ) {
    ideaCountByPhaseAndDays(
      channelId: $channelId
      challengeId: $challengeId
      spaceId: $spaceId
    ) {
      days
      stageCount {
        phase
        items
      }
    }
  }
`;

export const GET_IDEA_COUNT_BY_CHANNEL_TAGS = gql`
  query IdeaCountByChannelTags(
    $channelId: UUID!
    $challengeId: UUID
    $spaceId: UUID
  ) {
    ideaCountByChannelTags(
      channelId: $channelId
      challengeId: $challengeId
      spaceId: $spaceId
    ) {
      channelTag
      count
    }
  }
`;

export const GET_IDEA_COUNT_BY_BUSINESS_AREAS = gql`
  query ChannelIdeaCountByBusinessAreas(
    $channelId: UUID!
    $challengeId: UUID
    $spaceId: UUID
  ) {
    channelIdeaCountByBusinessAreas(
      channelId: $channelId
      challengeId: $challengeId
      spaceId: $spaceId
    ) {
      businessArea
      count
    }
  }
`;

export const GET_CHANNEL_STATISTICS = gql`
  query getChannelStatistics($channelId: UUID!, $challengeId: UUID) {
    getChannelStatistics(channelId: $channelId, challengeId: $challengeId) {
      submittedItemsCount
      membersCount
      implementedItemsCount
      totalChallengesCount
    }
  }
`;

export const GET_IDEAS_COUNT_BY_REVIEWERS = gql`
  query channelIdeaEvaluationStatistics(
    $channelId: UUID!
    $challengeId: UUID
    $spaceId: UUID
  ) {
    channelIdeaEvaluationStatistics(
      channelId: $channelId
      challengeId: $challengeId
      spaceId: $spaceId
    ) {
      user {
        ...userBasics
      }
      pending
      completed
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const GET_IDEA_REVIEW_REQUEST = gql`
  query GetIdeaReviewRequest($ideaId: UUID!) {
    getIdeaReviewRequest(ideaId: $ideaId) {
      id
      user {
        ...userBasics
      }
      requestedBy {
        ...userBasics
      }
      idea {
        id
        name
        channel {
          id
        }
      }
      createdAt
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const GET_CHANNEL_IDEA = gql`
  query ChannelIdea($ideaId: UUID!) {
    channelIdea(ideaId: $ideaId) {
      ...idea
      criteria {
        ...criteria
      }
      criteriaResults {
        ...result
      }
      challenge {
        experts {
          ...userBasics
        }
      }
      currentPhase {
        phase {
          id
          title
        }
      }
      metrics {
        ...ideaMetric
      }
      metricTag
    }
  }
  ${IDEA}
  ${INNOVATION}
  ${IDEA_COLLABORATION}
  ${REGION}
  ${TOPIC}
  ${DISCUSSION}
  ${COUNTRY}
  ${IDEA_REPORTS}
  ${FILE}
  ${USER_BASICS}
  ${RANK}
  ${CRITERIA}
  ${CRITERIA_RESULT}
  ${IDEA_METRIC}
  ${METRIC}
  ${PROJECT}
  ${UNIT}
`;

export const GET_CHANNEL_IDEAS_BY_PHASE = gql`
  query ChannelIdeasByPhase(
    $channelId: UUID!
    $phaseId: UUID!
    $filterBy: ChannelIdeasFilter
    $page: Int!
    $limit: Int!
    $isAsc: Boolean
  ) {
    channelIdeasByPhase(
      channelId: $channelId
      phaseId: $phaseId
      filterBy: $filterBy
      page: $page
      limit: $limit
      isAsc: $isAsc
    ) {
      items {
        id
        name
        age
        channelTag {
          name
        }
        createdBy {
          ...userBasics
        }
        metricTag
        channelCriteria {
          ...criteria
        }
        channelCriteriaResults {
          ...channelCriteriaResults
        }
        ideaChannelMembers {
          user {
            ...userBasics
          }
        }
      }
      total
    }
  }
  ${FILE}
  ${USER_BASICS}
  ${CRITERIA}
  ${CHANNEL_IDEA_EVALUATION_RESULT}
`;

export const GET_CHANNEL_RELATED_CHALLENGE = gql`
  query ListRelatedChallenge($channelId: UUID) {
    listRelatedChallenge(channelId: $channelId) {
      id
      title
    }
  }
`;

export const GET_CHANNEL_RELATED_SPACES = gql`
  query ListRelatedInnovationSpace($channelId: UUID) {
    listRelatedInnovationSpace(channelId: $channelId) {
      id
      name
    }
  }
`;

export const GET_CHANNEL_RELATED_REVIEWERS = gql`
  query ListRelatedReviewers($channelId: UUID) {
    listRelatedReviewers(channelId: $channelId) {
      ...userBasics
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const GET_CHANNEL_RELATED_COUNTRIES = gql`
  query ListRelatedCountries($channelId: UUID) {
    listRelatedCountries(channelId: $channelId) {
      id
      name
    }
  }
`;

export const GET_CHANNEL_RELATED_BUSINESS_AREAS = gql`
  query ListRelatedBusinessAreas($channelId: UUID) {
    listRelatedBusinessAreas(channelId: $channelId)
  }
`;

export const GET_EVALUATION_IDEAS = gql`
  query ListEvaluationIdeas(
    $filterBy: ChannelIdeasFilter
    $isEvaluated: Boolean!
    $page: Int!
    $limit: Int!
  ) {
    listEvaluationIdeas(
      isEvaluated: $isEvaluated
      page: $page
      limit: $limit
      filterBy: $filterBy
    ) {
      items {
        ...ideaBasic
        countries {
          id
          name
        }
      }
      total
    }
  }
  ${IDEA_BASIC}
  ${INNOVATION}
  ${TOPIC}
  ${REGION}
  ${FILE}
  ${USER_BASICS}
  ${RANK}
  ${CRITERIA}
  ${CRITERIA_RESULT}
`;

export const GET_EVALUATION_IDEA_IDS = gql`
  query ListEvaluationIdeas(
    $filterBy: ChannelIdeasFilter
    $isEvaluated: Boolean!
    $page: Int!
    $limit: Int!
  ) {
    listEvaluationIdeas(
      isEvaluated: $isEvaluated
      page: $page
      limit: $limit
      filterBy: $filterBy
    ) {
      items {
        id
      }
      total
    }
  }
`;

export const GET_DASHBOARD_IDEAS_STRATEGIC_DRIVERS = gql`
  query DashboardIdeaStrategicDrivers(
    $channelId: UUID
    $challengeId: UUID
    $spaceId: UUID
  ) {
    dashboardIdeaStrategicDrivers(
      channelId: $channelId
      challengeId: $challengeId
      spaceId: $spaceId
    ) {
      name
      count
    }
  }
`;

export const GET_DASHBOARD_IDEA_DELIVERY_DURATION = gql`
  query DashboardIdeaDeliveryDuration(
    $channelId: UUID
    $challengeId: UUID
    $spaceId: UUID
  ) {
    dashboardIdeaDeliveryDuration(
      channelId: $channelId
      challengeId: $challengeId
      spaceId: $spaceId
    ) {
      name
      count
    }
  }
`;

export const DELETE_PHASE = gql`
  mutation DeletePhase($id: UUID!) {
    deletePhase(id: $id)
  }
`;

export const DELETE_CHANNEL = gql`
  mutation DeleteChannel($id: UUID!) {
    deleteChannel(id: $id)
  }
`;

export const GET_CHANNEL_CHALLENGES = gql`
  query ChannelChallenges($id: UUID!) {
    channelChallenges(id: $id) {
      id
      title
      state
    }
  }
`;
